import * as React from 'react'
import { cn } from '@/utilities/cn'
import { Container } from '@/components/Container'

export type HeroSectionVariant = 'gradient' | 'dark' | 'light'

export interface HeroStat {
  value: string
  label: string
}

export interface HeroSectionProps {
  title: string
  subtitle?: string
  variant?: HeroSectionVariant
  stats?: HeroStat[]
  className?: string
  children?: React.ReactNode
}

const variantClasses: Record<HeroSectionVariant, string> = {
  gradient: 'bg-gradient-to-br from-primary to-accent text-white',
  dark: 'bg-slate-900 text-white',
  light: 'bg-white text-slate-800',
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  variant = 'gradient',
  stats,
  className,
  children,
}) => {
  return (
    // Hero is full-bleed regardless of any parent max-width container —
    // `relative left-1/2 -mx-[50vw] w-screen` escapes the wrapper to span
    // the viewport. Same trick ColorBand uses for fullBleed=true. Requires
    // an ancestor with overflow-x: hidden (set on body in globals.scss for
    // the frontend layout — embed routes / custom shells must verify).
    <section className={cn(variantClasses[variant], 'py-20 relative left-1/2 -mx-[50vw] w-screen', className)}>
      <Container>
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-6xl font-kadwa font-bold mb-6">{title}</h1>
          {subtitle ? (
            <p className={cn('text-xl lg:text-2xl mb-8', variant === 'light' ? 'text-slate-600' : 'text-white/90')}>
              {subtitle}
            </p>
          ) : null}
          {children}
          {stats && stats.length > 0 ? (
            <div
              className={cn(
                'grid grid-cols-1 gap-8 mt-12',
                stats.length === 1 && 'md:grid-cols-1',
                stats.length === 2 && 'md:grid-cols-2',
                stats.length === 3 && 'md:grid-cols-3',
                stats.length >= 4 && 'md:grid-cols-2 lg:grid-cols-4',
              )}
            >
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-2xl p-6 border',
                    variant === 'light'
                      ? 'bg-slate-50 border-slate-200'
                      : 'bg-white/10 backdrop-blur-sm border-white/20',
                  )}
                >
                  <div className="text-3xl lg:text-4xl font-bold mb-2">{stat.value}</div>
                  <div className={variant === 'light' ? 'text-slate-600' : 'text-white/80'}>{stat.label}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Container>
    </section>
  )
}

export default HeroSection
